import React, { useEffect, useMemo, useState } from "react";
import { ListPageLayout } from "@/components/cadastro/ListPageLayout";
import { DataTable, ColumnDef } from "@/components/cadastro/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Mail, MessageCircle, Pencil, KeyRound, Ban, Plus, Check } from "lucide-react";
import { NovoUsuarioModal } from "@/components/usuario/NovoUsuarioModal";
import { listMembros, getIniciais } from "@/services/profilesCache";
import { ROTULOS_PAPEIS } from "@/domain/papeis";
import { getObras } from "@/services/obraStorageService";

interface Usuario {
  id: string;
  nome: string;
  avatarUrl?: string;
  emails: string[];
  telefones: string[];
  funcao: string; // Diretor, Gestor, Engenheiro, Mestre, Administrativo, Cliente/Visitante
  obras: string[];
  status: "Ativo" | "Convidado" | "Desativado" | "Bloqueado";
  ultimoAcesso?: string; // ISO
  notificacoes: { app: boolean; email: boolean; whatsapp: boolean };
  endereco?: string;
  idioma?: string;
  fuso?: string;
}

// Rótulos vindos do mapa de papéis (src/domain/papeis.ts), que é a mesma fonte
// que o banco usa via `work_users.role`. Antes era um array de strings sem
// ligação nenhuma com o enum do Postgres.
const FUNCOES = ROTULOS_PAPEIS;

const STATUS_OPCOES: Usuario["status"][] = [
  "Ativo",
  "Convidado",
  "Desativado",
  "Bloqueado",
];

// Campos que a tela exibe e a tabela `profiles` não carrega (telefone,
// notificações, endereço, idioma, fuso, último acesso). Ficam vazios em vez de
// receberem valor inventado — a tela mostra "—" e fica evidente o que ainda não
// tem origem no banco.
const SEM_ORIGEM_NO_BANCO = {
  telefones: [] as string[],
  obras: [] as string[],
  notificacoes: { app: false, email: false, whatsapp: false },
} satisfies Partial<Usuario>;

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
};

const firstPlusN = (arr: string[]) => {
  if (!arr || arr.length === 0) return "—";
  if (arr.length === 1) return arr[0];
  return `${arr[0]} +${arr.length - 1}`;
};

const toWhatsAppLink = (t?: string) => {
  const n = (t || "").replace(/\D/g, "");
  const withCountry = n.startsWith("55") ? n : `55${n}`;
  return `https://wa.me/${withCountry}`;
};

// Alvo de toque de 44px sem inchar o visual. No mobile o botão do checkbox
// ocupa 44x44 de verdade — é o que o dedo acerta e o que a bateria mede — mas a
// borda sai dele e vai para um pseudo-elemento de 16px no centro, então o
// quadradinho continua do mesmo tamanho. Mesmo truque dos botões de remover tag
// do modal de tarefa. No desktop o componente volta ao padrão do shadcn.
const ALVO_TOQUE = cn(
  "relative flex h-11 w-11 items-center justify-center border-0 bg-transparent",
  "data-[state=checked]:bg-transparent",
  "before:absolute before:left-1/2 before:top-1/2 before:h-4 before:w-4",
  "before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-sm",
  "before:border before:border-primary before:content-['']",
  "data-[state=checked]:before:bg-primary",
  "[&>span]:relative [&>span]:z-10",
  "sm:h-4 sm:w-4 sm:border sm:before:hidden",
  "sm:data-[state=checked]:bg-primary"
);

const statusPill = (s: Usuario["status"]) =>
  cn(
    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
    s === "Ativo" && "bg-primary/10 text-primary",
    s === "Convidado" && "bg-secondary/50 text-foreground",
    s === "Desativado" && "bg-muted text-muted-foreground",
    s === "Bloqueado" && "bg-destructive/10 text-destructive"
  );

const UsuariosPermissoes = () => {
  // SEO title
  useEffect(() => {
    document.title = "Cadastro - Usuários e Permissões";
  }, []);

  // Membros reais da equipe (`profiles`, hidratada no login pelo hydrateAll),
  // mesmo cache que a delegação de tarefas já usa. Antes eram dois usuários
  // fictícios em memória, que sumiam ao recarregar.
  const [usuarios, setUsuarios] = useState<Usuario[]>(() =>
    listMembros().map((m) => ({
      id: m.id,
      nome: m.nome,
      avatarUrl: m.avatarUrl ?? undefined,
      emails: m.email ? [m.email] : [],
      // `funcao` sai de work_users.role, que a tela ainda não lê — fica vazia em
      // vez de rotular todo mundo de "Engenheiro".
      funcao: "",
      // Conta com perfil é conta existente; o banco não distingue convidado de
      // ativo hoje.
      status: "Ativo",
      ...SEM_ORIGEM_NO_BANCO,
    }))
  );

  // Obras reais para os seletores de vínculo (antes: quatro nomes fixos
  // "Obra 1 - Centro", "Obra 2 - Zona Sul"…).
  const todasObras = useMemo(
    () => getObras().map((o) => o.nomeObra || "Obra sem nome"),
    []
  );

  // Search & filters
  const [search, setSearch] = useState("");
  const [fFuncao, setFFuncao] = useState<string | "todas">("todas");
  const [fObras, setFObras] = useState<string[]>([]);
  const [fStatus, setFStatus] = useState<Usuario["status"] | "todos">("todos");
  const [fUltAcesso, setFUltAcesso] = useState<"Hoje" | "7 dias" | "30 dias" | "Custom" | "Todos">("Todos");

  // Selection (bulk actions)
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Add user modal
  const [addOpen, setAddOpen] = useState(false);

  // Edit drawer
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);

  // Loading (skeleton demo)
  const [loading, setLoading] = useState(false);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    let list = usuarios.filter((u) =>
      `${u.nome} ${u.emails?.join(" ")} ${u.telefones?.join(" ")}`.toLowerCase().includes(s)
    );
    if (fFuncao !== "todas") list = list.filter((u) => u.funcao === fFuncao);
    if (fStatus !== "todos") list = list.filter((u) => u.status === fStatus);
    if (fObras.length > 0) list = list.filter((u) => u.obras.some((o) => fObras.includes(o)));
    if (fUltAcesso !== "Todos") {
      const now = Date.now();
      const delta = fUltAcesso === "Hoje" ? 1 : fUltAcesso === "7 dias" ? 7 : fUltAcesso === "30 dias" ? 30 : undefined;
      if (delta) {
        const min = now - delta * 24 * 60 * 60 * 1000;
        list = list.filter((u) => (u.ultimoAcesso ? new Date(u.ultimoAcesso).getTime() >= min : false));
      }
    }
    return list;
  }, [usuarios, search, fFuncao, fObras, fStatus, fUltAcesso]);

  const clearSelection = () => setSelected(new Set());

  // Inline update helper for DataTable render callbacks
  const updateRow = (id: string, partial: Partial<Usuario>) => {
    setUsuarios((prev) => prev.map((u) => (u.id === id ? { ...u, ...partial } : u)));
  };

  // Columns config
  type Row = Usuario;
  const columns: ColumnDef<Row>[] = [
    {
      key: "__select__" as any,
      header: "",
      // 44px de alvo de toque não cabem numa coluna de 40px.
      className: "w-14 sm:w-10",
      render: (_v, r) => (
        <Checkbox
                          className={ALVO_TOQUE}
          checked={selected.has(r.id)}
          onCheckedChange={(ck) => {
            setSelected((prev) => {
              const next = new Set(prev);
              if (ck) next.add(r.id);
              else next.delete(r.id);
              return next;
            });
          }}
          aria-label={`Selecionar ${r.nome}`}
        />
      ),
    },
    {
      key: "nome",
      header: "Usuário",
      className: "min-w-[220px]",
      render: (_v, r) => (
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={r.avatarUrl} alt={`Avatar de ${r.nome}`} />
            <AvatarFallback>{r.nome ? getIniciais(r.nome) : "?"}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{r.nome}</span>
            <span className="text-xs text-muted-foreground">{r.funcao}</span>
          </div>
        </div>
      ),
    },
    {
      key: "emails",
      header: "E-mail principal",
      className: "min-w-[220px]",
      render: (_v, r) => (
        r.emails?.[0] ? (
          <a
            href={`mailto:${r.emails[0]}`}
            className="inline-flex min-h-[44px] items-center underline underline-offset-2 sm:min-h-0"
          >
            {r.emails[0]}
          </a>
        ) : (
          <span>—</span>
        )
      ),
    },
    {
      key: "telefones",
      header: "Telefones",
      className: "min-w-[160px]",
      render: (_v, r) => (
        <div className="flex items-center gap-2">
          <span>{firstPlusN(r.telefones || [])}</span>
          {r.telefones?.[0] && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={`whatsapp://send?phone=55${r.telefones[0].replace(/\D/g, '')}&text=Olá!`}
                    aria-label={`Abrir WhatsApp para ${r.telefones[0]}`}
                    className="inline-flex"
                  >
                    <MessageCircle className="h-4 w-4 text-green-600 hover:text-green-700" />
                  </a>
                </TooltipTrigger>
                <TooltipContent className="bg-popover">WhatsApp</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
    {
      key: "funcao",
      header: "Função/Perfil",
      className: "min-w-[180px]",
      render: (_v, r) => (
        <span className="text-foreground">{r.funcao}</span>
      ),
    },
    {
      key: "obras",
      header: "Obras vinculadas",
      className: "min-w-[200px]",
      render: (_v, r) => {
        const count = r.obras?.length || 0;
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="rounded-full">
                    {count} obra{count === 1 ? "" : "s"}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="bg-popover">
                  <div className="max-w-[280px] text-sm">
                    {r.obras && r.obras.length > 0 ? r.obras.join(", ") : "Sem obras"}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-11 sm:h-9">
                  Editar
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 bg-popover">
                <div className="space-y-2">
                  <Label className="text-sm">Selecionar obras</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {todasObras.map((o) => {
                      const checked = r.obras?.includes(o);
                      return (
                        <label key={o} className="flex items-center gap-2 text-sm">
                          <Checkbox
                          className={ALVO_TOQUE}
                            checked={checked}
                            onCheckedChange={(ck) => {
                              const next = new Set(r.obras || []);
                              if (ck) next.add(o);
                              else next.delete(o);
                              updateRow(r.id, { obras: Array.from(next) });
                            }}
                          />
                          <span>{o}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      className: "min-w-[160px]",
      render: (_v, r, onRowChange) => (
        <Select
          defaultValue={r.status}
          onValueChange={(val: Usuario["status"]) => onRowChange?.({ status: val } as Partial<Row>)}
        >
          <SelectTrigger className="h-11 sm:h-9">
            <div className={statusPill(r.status)}>{r.status}</div>
          </SelectTrigger>
          <SelectContent className="z-50 bg-popover">
            {STATUS_OPCOES.map((s) => (
              <SelectItem key={s} value={s}>
                <div className={statusPill(s)}>{s}</div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      key: "ultimoAcesso",
      header: "Último acesso",
      className: "min-w-[140px]",
      render: (v) => <span>{formatDate(v as string)}</span>,
    },
    {
      key: "notificacoes",
      header: "Notificações",
      className: "min-w-[140px]",
      render: (_v, r) => (
        <div className="flex items-center gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Mail className={cn("h-4 w-4", r.notificacoes.email ? "text-primary" : "text-muted-foreground")} />
              </TooltipTrigger>
              <TooltipContent className="bg-popover">E-mail</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <MessageCircle className={cn("h-4 w-4", r.notificacoes.whatsapp ? "text-primary" : "text-muted-foreground")} />
              </TooltipTrigger>
              <TooltipContent className="bg-popover">WhatsApp</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <svg viewBox="0 0 24 24" className={cn("h-4 w-4", r.notificacoes.app ? "text-primary" : "text-muted-foreground")}>
                  <circle cx="12" cy="12" r="10" fill="currentColor" opacity=".15" />
                  <path d="M7 17h10M12 7v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </TooltipTrigger>
              <TooltipContent className="bg-popover">App</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
    {
      key: "id",
      header: "Ações",
      className: "w-[140px] text-right",
      render: (_v, r) => (
        <div className="flex items-center justify-end gap-1.5">
          {/* Editar */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 sm:h-9 sm:w-9"
                  aria-label="Editar usuário"
                  onClick={() => {
                    setEditing(r);
                    setEditOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-popover">Editar</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Resetar senha */}
          <AlertDialog>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-9 sm:w-9" aria-label="Resetar senha">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent className="bg-popover">Reenviar senha</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <AlertDialogContent className="bg-background">
              <AlertDialogHeader>
                <AlertDialogTitle>Reenviar instruções de senha?</AlertDialogTitle>
                <AlertDialogDescription>
                  Enviar instruções de redefinição de senha para {r.emails?.[0] || "este usuário"}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => toast({ title: "Reset de senha", description: `Instruções enviadas para ${r.emails?.[0] || "usuário"}` })}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Ativar/Desativar */}
          <AlertDialog>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-11 w-11 sm:h-9 sm:w-9" aria-label={r.status === "Desativado" ? "Ativar usuário" : "Desativar usuário"}>
                      <Ban className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent className="bg-popover">{r.status === "Desativado" ? "Ativar" : "Desativar"}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <AlertDialogContent className="bg-background">
              <AlertDialogHeader>
                <AlertDialogTitle>{r.status === "Desativado" ? "Ativar usuário?" : "Desativar usuário?"}</AlertDialogTitle>
                <AlertDialogDescription>
                  {r.status === "Desativado" ? "O usuário voltará a ter acesso ao sistema." : "O usuário perderá o acesso ao sistema até ser reativado."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  updateRow(r.id, { status: r.status === "Desativado" ? "Ativo" : "Desativado" });
                  toast({ title: r.status === "Desativado" ? "Usuário ativado" : "Usuário desativado" });
                }}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ),
    },
  ];

  // Filters UI
  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={fFuncao} onValueChange={(v) => setFFuncao(v)}>
        <SelectTrigger className="h-11 sm:h-9 w-[200px]"><SelectValue placeholder="Função/Perfil" /></SelectTrigger>
        <SelectContent className="bg-popover">
          <SelectItem value="todas">Todas as funções</SelectItem>
          {FUNCOES.map((f) => (
            <SelectItem key={f} value={f}>{f}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-11 sm:h-9">
            Obras vinculadas {fObras.length > 0 && <Badge variant="secondary" className="ml-2 rounded-full">{fObras.length}</Badge>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 bg-popover">
          <div className="space-y-2">
            <Label className="text-sm">Filtrar por obras</Label>
            <div className="grid gap-2">
              {todasObras.map((o) => {
                const checked = fObras.includes(o);
                return (
                  <label key={o} className="flex items-center gap-2 text-sm">
                    <Checkbox
                          className={ALVO_TOQUE}
                      checked={checked}
                      onCheckedChange={(ck) =>
                        setFObras((prev) => {
                          const next = new Set(prev);
                          if (ck) next.add(o);
                          else next.delete(o);
                          return Array.from(next);
                        })
                      }
                    />
                    <span>{o}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Select value={fStatus} onValueChange={(v) => setFStatus(v as any)}>
        <SelectTrigger className="h-11 sm:h-9 w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
        <SelectContent className="bg-popover">
          <SelectItem value="todos">Todos status</SelectItem>
          {STATUS_OPCOES.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={fUltAcesso} onValueChange={(v) => setFUltAcesso(v as any)}>
        <SelectTrigger className="h-11 sm:h-9 w-[200px]"><SelectValue placeholder="Último acesso" /></SelectTrigger>
        <SelectContent className="bg-popover z-50">
          {(["Todos", "Hoje", "7 dias", "30 dias", "Custom"] as const).map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  // Bulk actions toolbar
  const bulkBar = selected.size > 0 && (
    <div className="mb-3 flex items-center justify-between rounded-md border border-border bg-muted/50 px-3 py-2">
      <div className="text-sm">
        {selected.size} selecionado{selected.size > 1 ? "s" : ""}
      </div>
      <div className="flex items-center gap-2">
        <Select onValueChange={(val) => { toast({ title: "Função atribuída", description: val }); clearSelection(); }}>
          <SelectTrigger className="h-11 sm:h-9 w-[180px]"><SelectValue placeholder="Atribuir função" /></SelectTrigger>
          <SelectContent className="bg-popover">
            {FUNCOES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-11 sm:h-9">Vincular a obras</Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 bg-popover">
            <div className="space-y-2">
              <Label className="text-sm">Selecionar obras</Label>
              <div className="grid gap-2">
                {todasObras.map((o) => (
                  <label key={o} className="flex items-center gap-2 text-sm">
                    <Checkbox className={ALVO_TOQUE} />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
              <Button size="sm" className="h-11 sm:h-9" onClick={() => { toast({ title: "Obras vinculadas" }); clearSelection(); }}>Aplicar</Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => { toast({ title: "Status atualizado" }); clearSelection(); }}>Ativar/Desativar</Button>
        <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => { toast({ title: "Convite reenviado" }); clearSelection(); }}>Reenviar convite</Button>
        <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => { toast({ title: "MFA resetado" }); clearSelection(); }}>Resetar MFA</Button>
      </div>
    </div>
  );


  // Edit Drawer (simplificado com abas)
  const EditDrawer = (
    <Drawer open={editOpen} onOpenChange={setEditOpen}>
      <DrawerContent className="bg-background">
        <div className="mx-auto w-full max-w-5xl p-4">
          <DrawerHeader>
            <DrawerTitle>Editar Usuário</DrawerTitle>
            <DrawerDescription>Atualize dados, permissões, obras e notificações.</DrawerDescription>
          </DrawerHeader>
          <Tabs defaultValue="perfil" className="mt-2">
            <TabsList>
              <TabsTrigger value="perfil" className="h-11 sm:h-9">Perfil</TabsTrigger>
              <TabsTrigger value="permissoes" className="h-11 sm:h-9">Permissões</TabsTrigger>
              <TabsTrigger value="obras" className="h-11 sm:h-9">Obras</TabsTrigger>
              <TabsTrigger value="notifs" className="h-11 sm:h-9">Notificações</TabsTrigger>
              <TabsTrigger value="seguranca" className="h-11 sm:h-9">Segurança</TabsTrigger>
              <TabsTrigger value="auditoria" className="h-11 sm:h-9">Auditoria</TabsTrigger>
            </TabsList>
            <TabsContent value="perfil" className="pt-4">
              {!editing ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nome</Label>
                    <Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} />
                  </div>
                  <div>
                    <Label>E-mail principal</Label>
                    <Input value={editing.emails?.[0] || ""} onChange={(e) => setEditing({ ...editing, emails: [e.target.value, ...(editing.emails?.slice(1) || [])] })} />
                  </div>
                  <div>
                    <Label>Telefone principal</Label>
                    <Input value={editing.telefones?.[0] || ""} onChange={(e) => setEditing({ ...editing, telefones: [e.target.value, ...(editing.telefones?.slice(1) || [])] })} />
                  </div>
                  <div>
                    <Label>Função</Label>
                    <Select value={editing.funcao} onValueChange={(v) => setEditing({ ...editing, funcao: v })}>
                      <SelectTrigger className="h-11 sm:h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">
                        {FUNCOES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={editing.status} onValueChange={(v: Usuario["status"]) => setEditing({ ...editing, status: v })}>
                      <SelectTrigger className="h-11 sm:h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">
                        {STATUS_OPCOES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="permissoes" className="pt-4">
              <div className="mb-3 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm"><Switch defaultChecked /> Herdar da Função</label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={() => toast({ title: "Modelo salvo" })}>Salvar como Modelo</Button>
                  <Button size="sm" className="h-11 sm:h-9" onClick={() => toast({ title: "Sugestão criada" })}>Sugerir permissões</Button>
                </div>
              </div>
              <div className="overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left">Módulo</th>
                      {["Visualizar","Criar","Editar","Aprovar","Excluir","Exportar","Configurar"].map((a) => (
                        <th key={a} className="px-3 py-2 text-center">{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {["Diário de Obra","Gestor de Tarefas","Ocorrências","Fotos/Vídeos/Anexos","Materiais","Equipamentos","Cadastros","Painel da Obra","Dashboard Geral","Relatórios","Relatórios 2","Agente/Obra IA","Configurações"].map((m) => (
                      <tr key={m} className="border-t">
                        <td className="px-3 py-2">{m}</td>
                        {Array.from({ length: 7 }).map((_, i) => (
                          <td key={i} className="px-3 py-2 text-center">
                            <Checkbox className={ALVO_TOQUE} defaultChecked={Math.random() > 0.6} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="obras" className="pt-4">
              {!editing ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(editing.obras || []).map((o) => (
                      <Badge key={o} variant="secondary" className="gap-1">
                        {o}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 ml-1"
                          onClick={() => setEditing({ ...editing, obras: (editing.obras || []).filter((x) => x !== o) })}
                          aria-label={`Remover ${o}`}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 sm:h-9"><Plus className="h-4 w-4" /> Adicionar</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-popover">
                      <div className="grid gap-2">
                        {todasObras.map((o) => (
                          <label key={o} className="flex items-center gap-2 text-sm">
                            <Checkbox className={ALVO_TOQUE} checked={!!editing.obras?.includes(o)} onCheckedChange={(ck) => {
                              const set = new Set(editing.obras || []);
                              if (ck) set.add(o); else set.delete(o);
                              setEditing({ ...editing, obras: Array.from(set) });
                            }} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </TabsContent>

            <TabsContent value="notifs" className="pt-4">
              {!editing ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <div className="space-y-4">
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm"><Switch checked={editing.notificacoes.app} onCheckedChange={(ck) => setEditing({ ...editing, notificacoes: { ...editing.notificacoes, app: !!ck } })} /> App</label>
                    <label className="flex items-center gap-2 text-sm"><Switch checked={editing.notificacoes.email} onCheckedChange={(ck) => setEditing({ ...editing, notificacoes: { ...editing.notificacoes, email: !!ck } })} /> E-mail</label>
                    <label className="flex items-center gap-2 text-sm"><Switch checked={editing.notificacoes.whatsapp} onCheckedChange={(ck) => setEditing({ ...editing, notificacoes: { ...editing.notificacoes, whatsapp: !!ck } })} /> WhatsApp</label>
                  </div>
                  <div className="text-sm text-muted-foreground">Eventos: nova tarefa, alteração de status, atraso, novo comentário, ocorrência, RDO aguardando aprovação, etc.</div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="seguranca" className="pt-4">
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => toast({ title: "Reset de senha enviado" })}><KeyRound className="h-4 w-4" /> Resetar senha</Button>
                <Button variant="outline" onClick={() => toast({ title: "MFA atualizado" })}>Ativar/Desativar MFA</Button>
                <Button variant="outline" onClick={() => toast({ title: "Logout forçado" })}>Forçar logout</Button>
                <Button variant="destructive" onClick={() => toast({ title: "Usuário bloqueado" })}><Ban className="h-4 w-4" /> Bloquear</Button>
              </div>
            </TabsContent>

            <TabsContent value="auditoria" className="pt-4">
              <div className="space-y-2 text-sm">
                <div>• 10:12 - Convite enviado para maria@ex.com</div>
                <div>• 09:48 - Acesso realizado (IP 200.200.200.200)</div>
                <div>• Ontem - Permissões atualizadas (Gestor → Engenheiro)</div>
              </div>
            </TabsContent>
          </Tabs>
          <DrawerFooter className="mt-4">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button onClick={() => { if (editing) updateRow(editing.id, editing); toast({ title: "Usuário atualizado" }); setEditOpen(false); }}>Salvar</Button>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );

  return (
    <section className="flex-1">
      <ListPageLayout
        title="Usuários e Permissões"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome, e-mail, telefone…"
        onAdd={() => setAddOpen(true)}
        filters={filters}
      >
        {bulkBar}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed p-8 text-center">
            <div className="text-sm text-muted-foreground">Sem usuários ainda</div>
            <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Adicionar</Button>
          </div>
        ) : (
          <DataTable data={filtered} setData={setUsuarios} columns={columns} pageSize={8} dense />
        )}
        <NovoUsuarioModal 
          open={addOpen}
          onOpenChange={setAddOpen}
        />
        {EditDrawer}
      </ListPageLayout>
    </section>
  );
};

export default UsuariosPermissoes;
